<template>
  <step-template :subStep="subStep">
    <v-card-text>
      <div>
        <br />
        <h4 v-if="_useRandomizer === false" class="pb-15 text-center" v-html="description">
        </h4>
        <h3 v-else class="pt-5 pb-7 text-center">
          The
          <span style="color:#ff3f3f;"><b>RANDOMIZER</b></span>
          has selected:
          <br />
          <span class="randomizer-value pt-3">{{ _name }}: {{ tempValue }}<span v-html="unit"></span></span>
        </h3>
        <br />
        <p class="mr-5 ml-5">
          <v-slider
            v-model="tempValue"
            :min="_min"
            :max="_max"
            :step="_stepSize" 
            persistent-hint
            thumb-label="always"
            color="blue"
            ticks
            :disabled="_useRandomizer"
            @change="sliderChanged"
          ></v-slider>
          <v-range-slider
            class="slider-scale"
            :tick-labels="cThickLabels"
            :step="cThickLabelsStep"
            :min="_min"
            :max="_max"
            :disabled="true"
          >
          </v-range-slider>
        </p>
        <v-row>
          <v-col v-if="imgSrc" cols="4" sm="5" class="slider-image d-flex justify-center items-center">
              <img :src="imgSrc" class="pl-0 pl-sm-8" />
          </v-col>
          <v-col cols="8" sm="7" class="d-flex slider-value-text">
            <h2 v-if="_useRandomizer === false && firstSet" class="pl-8 slider-selection">
              <span class="selection-label">Selected {{ _name }}:</span>
                <br />
              <span class="selection-value">{{ sliderValue }}<span v-html="unit"></span></span>
            </h2>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </step-template>
</template>

<script>
/**
 * To use:
 - in the database, go to table substeps
 - find the substep row that you want this component
 - update the row field component='step-slider'
 - update the row field component_props as a JSON like:
{
  "variables": { "vmodel": "CUSTOM_VARIABLE_NAME", "CUSTOM_VARIABLE_NAME": CUSTOM_VARIABLE_DEFAULT_VALUE },
  "min":"4",
  "max":"12",
  "description": "Select your patient's A1C by moving to the selected value."
  "imgSrc": "/img/A1C.95cb81e4.jpg"
}
*/

import { mapState, mapActions, mapMutations } from "vuex";
import StepTemplate from "/src/components/steps/StepTemplate.vue";

export default {
  name: "StepSlider",
  components: {
    StepTemplate,
  },
  props: {
    step: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    subStep: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    variables: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
    imgSrc: {
      type: String,
      required: false,
      default: "",
    },
    description: {
      type: String,
      required: false,
      default: "",
    },
    min: {
      type: String,
      required: false,
      default: "0",
    },
    max: {
      type: String,
      required: false,
      default: "100",
    },
    name: {
      type: String,
      required: false,
      default: "",
    },
    unit: {
      type: String,
      required: false,
      default: "",
    },
    stepSize: {
      type: String,
      required: false,
      default: "0.1",
    },
    showSteps: {
      type: Boolean,
      default: true,
      required: false,
    },
    supportRandomizer: {
      type: Boolean,
      default: true,
      required: false,
    },
    setDefaultAtAvatar: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      sliderValue: 0,
      tempValue: 0,
      firstSet: false,
      avatarOverridedVariables: {
        min: null,
        max: null,
        vModelValue: null,
        defaultValue: null
      },
      avatarVuexVariables: [],
    };
  },
  computed: {
    ...mapState({
      avatar: (state) => state.avatar,
      caseData: (state) => state.caseData,
    }),
    avatarVariables() {
      if (JSON.parse(this.avatar.variables)) {
        return JSON.parse(this.avatar.variables);
      }
      return null;
    },
    _min() {
      const val = this.avatarOverridedVariables.min
        ? this.avatarOverridedVariables.min
        : this.min;
      return Number.parseFloat(val);
    },
    _max() {
      const val = this.avatarOverridedVariables.max
        ? this.avatarOverridedVariables.max
        : this.max;
      return Number.parseFloat(val);
    },
    _stepSize() {
      const val = this.avatarOverridedVariables.stepSize
        ? this.avatarOverridedVariables.stepSize
        : this.stepSize;
      return Number.parseFloat(val);
    },    
    _name() {
      return this.name
        ? this.name
        : this.variables.vmodel.toUpperCase();
    },
    _useRandomizer() {
      return this.supportRandomizer
        ? this.avatar.random
        : false;
    },
    optimalLabelCount() {
      if (!this.showSteps) return 2;
      
      const min = this._min;
      const max = this._max;
      const step = this._stepSize;
      
      const decimals = Math.max(
        this.getDecimalPlaces(min),
        this.getDecimalPlaces(max),
        this.getDecimalPlaces(step)
      );
      const scale = Math.pow(10, decimals);
      
      const minInt = Math.round(min * scale);
      const maxInt = Math.round(max * scale);
      const stepInt = Math.max(1, Math.round(step * scale));
      const rangeInt = maxInt - minInt;
      
      // Try label counts from 9 (7 inner + min/max) down to 4 (2 inner + min/max)
      // Pick highest that aligns with step size
      for (let count = 9; count >= 4; count--) {
        const segments = count - 1;
        const spacingInt = rangeInt / segments;
        // Check if spacing is a whole number and divisible by stepInt
        if (Number.isInteger(spacingInt) && spacingInt % stepInt === 0) {
          return count;
        }
      }
      return 6; // Fallback to 6 labels (4 inner + min/max) even if spacing doesn't align
    },
    cThickLabels() {
      const count = this.optimalLabelCount;
      const labels = [];
      const step = this.cThickLabelsStep;
      
      // Generate exactly the number of labels that match tick positions
      for (let i = 0; i < count; i++) {
        const value = this._min + (step * i);
        labels.push(this.roundLabelNumber(value, this._stepSize));
      }
      
      // Ensure the last label is exactly the max value (avoid floating-point drift)
      if (labels.length > 0) {
        labels[labels.length - 1] = this._max;
      }
      
      return labels;
    },
    cThickLabelsStep() {
      // Use the optimal label count to determine spacing
      const count = this.optimalLabelCount;
      const range = this._max - this._min;
      const segments = count - 1;
      return range / segments;
    },
  },
  watch: {
    sliderValue(val) {
      if (this._useRandomizer) {
        this.firstSet = true;
        this.setSliderValue(val);
        this.manageContinueButton();
      } else {
      this.manageContinueButton();
     }

    },
  },
  created() {
    this.manageContinueButton();
    this.setAvatarOverridedVariables();
    this.setInitialSliderValue();
  },
  methods: {
    ...mapMutations(["setContinueButtonEnabled"]),
    ...mapActions(["setCaseDataValue"]),
    manageContinueButton() {
      if (this.sliderValue === 0) this.setContinueButtonEnabled(false);
      if (!this.firstSet) this.setContinueButtonEnabled(false);
      else this.setContinueButtonEnabled(true);
    },
    getDecimalPlaces(value) {
      const str = String(value);
      if (!str.includes('.')) return 0;
      return str.split('.')[1].length;
    },
    roundLabelNumber(num, stepSize) {
      const decimals = Math.max(this.getDecimalPlaces(num), this.getDecimalPlaces(stepSize));
      const scale = Math.pow(10, decimals);
      const numInt = Math.round(num * scale);
      const stepInt = Math.max(1, Math.round(stepSize * scale));
      const roundedInt = Math.round(numInt / stepInt) * stepInt;
      return roundedInt / scale;
    },
    setInitialSliderValue() {
      // Try to set currentStorageValue to CaseData
      const currentStorageValue = this.caseData[this.variables.vmodel];
      if (this._useRandomizer) {
        // Avatar Default Value
        const val = this.avatarOverridedVariables.vModelValue
        this.sliderValue = val;
        this.tempValue = val;
        this.firstSet = true;
      } else if (currentStorageValue) {
        // CaseData Storage
        this.sliderValue = currentStorageValue;
        this.tempValue = currentStorageValue;
        this.firstSet = true;
      } else if (this.avatarOverridedVariables.defaultValue) {
        // Avatar Default Value
        const val = this.avatarOverridedVariables.defaultValue
        this.sliderValue = val;
        this.tempValue = val;
      } else {
        // Substep Default Value
        const val = this.variables[this.variables.vmodel]
        this.sliderValue = val;
        this.tempValue = val;
      }
      this.manageContinueButton();
    },
    setSliderValue(val) {
      this.sliderValue = val;
      this.setCaseDataValue({ key: this.variables.vmodel, value: val, randomizer: this.avatar.random });
      this.setAvatarVuexVariablesValues(val);
    },
    sliderChanged(val) {
      this.firstSet = true;
      this.setSliderValue(val);
    },
    setAvatarOverridedVariables() {
      if (!this.avatarVariables || !this.avatarVariables.override) {
        return;
      }
      const override = this.avatarVariables.override;
      for (const i in override) {
        if (override[i].subStep && this.matchesSubStepId(override[i].subStep.id)) {
          if (override[i].subStep.addVuexVariable)
            this.addAvatarVuexVariable(override[i].subStep.addVuexVariable);
          if (override[i].subStep.props)
            this.addAvatarOverridedVariables(override[i].subStep.props);
        }
      }
    },
    matchesSubStepId(id) {
      // Support both single id and array of ids, with type coercion for string/number comparison
      const currentId = this.subStep.id;
      if (Array.isArray(id)) {
        return id.some(arrId => String(arrId) === String(currentId));
      }
      return String(id) === String(currentId);
    },
    addAvatarOverridedVariables(overrideProps) {
      for (const key in overrideProps) {
        this.avatarOverridedVariables[key] = overrideProps[key];
      }
    },
    addAvatarVuexVariable(newVuexVariable) {
      for (const i in newVuexVariable) {
        this.avatarVuexVariables.push(newVuexVariable[i]);
        const varName = newVuexVariable[i].name;

        if (!this.caseData[varName]) {
          let firstValue = newVuexVariable[i].value;
          if (typeof firstValue === "object") {
            firstValue = Object.keys(firstValue)[0];
          }

          // this.setCaseDataValue({
          //   key: varName,
          //   value: firstValue,
          //   randomizer: this.avatar.random,
          // });
        }
      }
    },
    setAvatarVuexVariablesValues(sliderValue) {
      for (const i in this.avatarVuexVariables) {
        const variableObject = this.avatarVuexVariables[i];
        if (variableObject.op === "range") {
          this.setAvatarVuexVariablesValuesRange(sliderValue, variableObject);
        }
      }
    },
    setAvatarVuexVariablesValuesRange(sliderValue, varObj) {
      const valuesObject = varObj.value;
      let valArray = [];
      let value = 999999999999999;

      for (const key in valuesObject) {
        valArray.push(valuesObject[key]);
      }
      valArray = valArray.sort().reverse();

      for (const key in valArray) {
        if (valArray[key] <= sliderValue && valArray[key] <= value) {
          value = valArray[key];
          break;
        }
      }

      for (const i in valuesObject) {
        if (valuesObject[i] === value) {
          value = i;
          break;
        }
      }

      this.setCaseDataValue({
        key: varObj.name,
        value: value,
        randomizer: this.avatar.random,
      });
    },
  },
};
</script>
<style>
.slider-image img {
  max-height:300px;
  max-width:390px;
  width: 100%;
  height: auto;
  object-fit: contain;
}
.randomizer-value {
  font-size: 1.5em;
  display: block;
}
.slider-scale {
  opacity: .7
}

.selection-label {
  font-size: 1.1em;
  margin-bottom: 20px;
}
.selection-value {
  font-size: 1.3em;
  margin-bottom: 20px;
}

/* Mobile styles */
@media (max-width: 600px) {
  .slider-image img {
    max-height: 180px;
    max-width: 100%;
  }
  .slider-selection {
    font-size: 1.3em;
  }
}
</style>